import { Link, useLocation } from "react-router-dom";
import { useMemo } from "react";
import { SITE_MEDIA } from "@/constants/siteMedia";
import { useSEO } from "@/hooks/useSEO";
import { SEO_BRAND } from "@/constants/seoConfig";

export default function NotFound() {
  const location = useLocation();
  const notFoundSeo = useMemo(
    () => ({
      title: `Page not found | ${SEO_BRAND}`,
      description:
        "This page is not part of AvachatAI. Explore Ava, pricing, FAQs, the blog, or download the app for emotional support on iOS and Android.",
      canonicalPath: location.pathname || "/",
      robots: "noindex, follow" as const,
    }),
    [location.pathname]
  );
  useSEO(notFoundSeo);
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 text-center relative overflow-hidden"
      style={{ background: "linear-gradient(160deg, #f5f0ff 0%, #ede8ff 60%, #f8f5ff 100%)" }}
    >
      {/* Background orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute w-96 h-96 rounded-full opacity-20 -top-20 -right-20" style={{ background: "radial-gradient(circle, #A855F7 0%, transparent 70%)" }} />
        <div className="absolute w-64 h-64 rounded-full opacity-15 bottom-10 left-10" style={{ background: "radial-gradient(circle, #7C3AED 0%, transparent 70%)" }} />
      </div>

      <div className="relative z-10 max-w-lg mx-auto">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="flex justify-center mb-8">
          <ol className="flex items-center gap-2 text-sm text-[#7a6aaa]" itemScope itemType="https://schema.org/BreadcrumbList">
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/" className="hover:text-[#7C3AED] transition-colors" itemProp="item"><span itemProp="name">Home</span></Link>
              <meta itemProp="position" content="1" />
            </li>
            <li><i className="ri-arrow-right-s-line"></i></li>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <span className="text-[#7C3AED] font-medium" itemProp="name">404</span>
              <meta itemProp="position" content="2" />
            </li>
          </ol>
        </nav>

        {/* Ava avatar */}
        <div className="w-24 h-24 rounded-full overflow-hidden mx-auto mb-6 border-4 border-[#e0d8ff]">
          <img
            src={SITE_MEDIA.avaIllustration}
            alt="Ava AI Companion"
            className="w-full h-full object-cover"
          />
        </div>

        <div
          className="text-8xl font-black mb-2 text-transparent bg-clip-text"
          style={{ backgroundImage: "linear-gradient(135deg, #7C3AED, #A855F7)" }}
        >
          404
        </div>

        <h1 className="font-display text-xl md:text-2xl font-bold text-[#1a1040] mb-3">
          Looks like this page got lost
        </h1>
        <p className="text-[#7a6aaa] text-base mb-8 leading-relaxed">
          Even Ava couldn&apos;t find this one. The page you&apos;re looking for doesn&apos;t exist or may have moved.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/"
            className="whitespace-nowrap flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white font-semibold text-sm cursor-pointer hover:opacity-90 transition-all hover:scale-105"
            style={{ background: "linear-gradient(135deg, #7C3AED, #A855F7)" }}
          >
            <i className="ri-home-line"></i>
            Back to Home
          </Link>
          <Link
            to="/blog"
            className="whitespace-nowrap flex items-center justify-center gap-2 px-6 py-3 rounded-full border-2 border-[#7C3AED]/30 text-[#7C3AED] font-semibold text-sm cursor-pointer hover:bg-[#7C3AED]/5 transition-all hover:scale-105"
          >
            <i className="ri-article-line"></i>
            Read our Blog
          </Link>
        </div>

        <p className="text-[#9a8aaa] text-xs mt-8">
          AvachatAI &mdash; Your AI companion, always here.
        </p>
      </div>
    </div>
  );
}
